import { useState, useRef, useEffect } from 'react';
import { Image } from 'antd';
import { ReportService } from '../../services/reports-services';

/**
 * MultiCheckedSelectBox component
 * Now the component handles for sales report only
 *
 * @param {string} placeholderText - Input placeholder text
 * @param {string} [boxClasses] - CSS classes for the select box (optional)
 * @param {string} [dropdownCardClasses] - CSS classes for dropdown box which open after click on select box (optional)
 * @param {Array} defaultSelects - Default selected options
 * @param {Array} options - Available options list
 * @param {Array} selectedOptions - Currently selected options
 * @param {Function} setSelectedOptions - Setter for selected options
 *
 * @returns {JSX.Element}
 */

export default function MultiCheckedSelectBox({
    context,
    placeholderText,
    boxClasses = '',
    dropdownCardClasses = '',
    defaultSelects = [],
    options,
    selectedOptions,
    setSelectedOptions
}) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);


    useEffect(() => {
        if (
            defaultSelects?.length > 0 &&
            selectedOptions.length === 0
        ) {
            setSelectedOptions(defaultSelects);
        }
    }, [defaultSelects, selectedOptions]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    function toggleDropdown() {
        setIsOpen(!isOpen);
    };

    function handleCheckboxChange(value) {
        setSelectedOptions(prev => {
            if (prev.includes(value)) {
                return prev.filter(item => item !== value);
            } else {
                return [...prev, value];
            }
        });
    };

    function getDisplayText() {
        if (selectedOptions.length === 0) {
            return <span style={{ color: '#999' }}>{placeholderText}</span>;
        }

        switch (context) {
            case "payment":
                return <span style={{ color: '#333' }}>{selectedOptions
                    .map(option =>
                        option.posBankId === null
                            ? option.paymentReceiveType
                            : ReportService.getPosBankLabel(options, option.posBankId)
                    )
                    .join(', ')}</span>;
            case "orderType":
                return <span style={{ color: '#333' }}>{selectedOptions
                    .map(option =>
                        option.posBankId === null
                            ? option.paymentReceiveType
                            : ReportService.getTakeoutTypeLabel(options, option)
                    )
                    .join(', ')}</span>;

        }
    };

    return (
        <div>
            <div style={styles.selectWrapper} ref={wrapperRef}>
                <div
                    className={boxClasses}
                    style={{
                        ...styles.selectTrigger,
                        ...(isOpen ? styles.selectTriggerActive : {})
                    }}
                    onClick={toggleDropdown}
                >
                    <span className='truncate'>{getDisplayText()}</span>
                    <span style={{
                        ...styles.arrow,
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>
                        <Image src='/images/icons/down.svg' alt='down' preview={false} />
                    </span>
                </div>

                {isOpen && (
                    <div className={dropdownCardClasses} style={styles.checkboxContainer}>
                        {options.map((option) => (
                            <label key={option.id} style={styles.checkboxItem}>
                                <input
                                    type="checkbox"
                                    checked={selectedOptions.includes(option.value)}
                                    onChange={() => handleCheckboxChange(option.value)}
                                    style={styles.hiddenCheckbox}
                                />
                                <span style={{
                                    ...styles.checkboxCustom,
                                    ...(selectedOptions.includes(option.value) ? styles.checkboxChecked : {})
                                }}>
                                    {selectedOptions.includes(option.value) && (
                                        <span style={styles.checkmark}></span>
                                    )}
                                </span>
                                <span style={styles.checkboxLabel}>{option.label}</span>
                            </label>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    selectWrapper: {
        width: '100%',
        maxWidth: '360px',
        position: 'relative',
    },
    selectTrigger: {
        background: 'white',
        border: '1px solid #e0e0e0',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        padding: '0 16px',
        fontSize: '15px',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        userSelect: 'none'
    },
    selectTriggerActive: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0
    },
    arrow: {
        fontSize: '14px',
        color: '#666',
        transition: 'transform 0.3s'
    },
    checkboxContainer: {
        background: 'white',
        border: '2px solid #e0e0e0',
        borderTop: '1px solid #e0e0e0',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        padding: '16px',
        width: '100%',
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 5,
        maxHeight: '300px',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    checkboxItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '10px 0',
        cursor: 'pointer',
        position: 'relative'
    },
    hiddenCheckbox: {
        display: 'none'
    },
    checkboxCustom: {
        paddingRight: '2px',
        width: '20px',
        height: '20px',
        border: '2px solid #d0d0d0',
        borderRadius: '0%',
        marginRight: '8px',
        position: 'relative',
        transition: 'all 0.3s ease',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    checkboxChecked: {
        background: '#00c853',
        borderColor: '#00c853'
    },
    checkmark: {
        width: '5px',
        height: '10px',
        border: 'solid white',
        borderWidth: '0 2.5px 2.5px 0',
        transform: 'rotate(45deg) translateY(-1px)',
        display: 'inline-block'
    },
    checkboxLabel: {
        fontSize: '15px',
        color: '#333',
        userSelect: 'none'
    }
};